import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService } from '../../../core/services/toast.service';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="fixed top-4 right-4 z-[9999] space-y-3 max-w-sm">
      @for (toast of toastService.toasts(); track toast.id) {
        <div 
          [class]="getToastClass(toast.type)"
          class="rounded-xl shadow-2xl p-4 border-l-4 backdrop-blur-sm animate-slide-in-right flex items-start gap-3 min-w-[320px]"
          (click)="toastService.remove(toast.id)">
          
          <!-- Ícone -->
          <div [class]="getIconClass(toast.type)" class="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0">
            @switch (toast.type) {
              @case ('success') {
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                </svg>
              }
              @case ('error') {
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              }
              @case ('warning') {
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
                </svg>
              }
              @case ('info') {
                <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
              }
            }
          </div>

          <!-- Conteúdo -->
          <div class="flex-1 min-w-0">
            <h4 class="font-bold text-sm mb-1" [class]="getTitleClass(toast.type)">{{ toast.title }}</h4>
            <p class="text-sm" [class]="getMessageClass(toast.type)">{{ toast.message }}</p>
          </div>

          <!-- Botão Fechar -->
          <button 
            (click)="toastService.remove(toast.id)"
            class="flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      }
    </div>
  `,
  styles: [`
    @keyframes slide-in-right {
      from {
        transform: translateX(100%);
        opacity: 0;
      }
      to {
        transform: translateX(0);
        opacity: 1;
      }
    }

    .animate-slide-in-right {
      animation: slide-in-right 0.3s ease-out;
    }
  `]
})
export class ToastComponent {
  toastService = inject(ToastService);

  getToastClass(type: string): string {
    const classes: Record<string, string> = {
      'success': 'bg-white dark:bg-gray-800 border-green-500',
      'error': 'bg-white dark:bg-gray-800 border-red-500',
      'warning': 'bg-white dark:bg-gray-800 border-yellow-500',
      'info': 'bg-white dark:bg-gray-800 border-blue-500'
    };
    return classes[type] || classes['info'];
  }

  getIconClass(type: string): string {
    const classes: Record<string, string> = {
      'success': 'bg-gradient-to-br from-green-500 to-green-600',
      'error': 'bg-gradient-to-br from-red-500 to-red-600',
      'warning': 'bg-gradient-to-br from-yellow-500 to-yellow-600',
      'info': 'bg-gradient-to-br from-blue-500 to-blue-600'
    };
    return classes[type] || classes['info'];
  }

  getTitleClass(type: string): string {
    const classes: Record<string, string> = {
      'success': 'text-green-900 dark:text-green-100',
      'error': 'text-red-900 dark:text-red-100',
      'warning': 'text-yellow-900 dark:text-yellow-100',
      'info': 'text-blue-900 dark:text-blue-100'
    };
    return classes[type] || classes['info'];
  }

  getMessageClass(type: string): string {
    return 'text-gray-700 dark:text-gray-300';
  }
}
